<template>
  <v-container ref="container" style="max-width: 95%;">
    <h1>Compare Doc Result: {{ uniqueId }}</h1>
    <v-btn
      color="success"
      class="mb-4"
      :href="outputUrl"
      target="_blank"
      :disabled="!isCompleted"
    >
      Export Output
    </v-btn>
    <v-tabs v-model="activeTab" background-color="primary" dark>
      <v-tab>Data Compare</v-tab>
      <v-tab>Item Listing</v-tab>
    </v-tabs>
    <v-tabs-items v-model="activeTab">
      <v-tab-item>
        <task-result-data-compare
          :table-height="tableHeight"
          :table-data="tableData"
          :get-cell-style="getCellStyle"
          @open-edit-dialog="openEditDialog"
        />
      </v-tab-item>
      <v-tab-item>
        <task-result-item-listing
          :table-height="tableHeight"
          :item-listing-data="itemListingData"
          :get-cell-style="getCellStyle"
          @toggle-row-collapse="toggleRowCollapse"
          @open-edit-dialog="openEditDialog"
        />
      </v-tab-item>
    </v-tabs-items>

    <!-- Edit Dialog -->
    <v-dialog v-model="editDialog" max-width="400px">
      <v-card>
        <v-card-title>Edit Cell Value</v-card-title>
        <v-card-text>
          <v-form ref="editForm" @submit.prevent="updateValue">
            <v-text-field
              v-model="editCell.newValue"
              label="New Value"
              outlined
              dense
              :rules="[v => !!v || 'Value is required']"
              @keyup.enter="updateValue"
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeDialog">Cancel</v-btn>
          <v-btn color="primary" @click="updateValue">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios'
import TaskResultDataCompare from './TaskResultDataCompare.vue'
import TaskResultItemListing from './TaskResultItemListing.vue'

export default {
  components: {
    TaskResultDataCompare,
    TaskResultItemListing
  },
  data() {
    return {
      taskId: this.$route.params.id,
      uniqueId: this.$route.params.uniqueId,
      activeTab: 0,
      loading: false,
      tableData: {
        data_compare: [],
        item_listing: []
      },
      itemListingData: {
        expand: [],
        collapse: [],
        isCollapsed: []
      },
      outputUrl: "",
      isCompleted: false,
      containerHeight: 0,
      // New data properties for editing
      editDialog: false,
      editCell: {
        tableType: '', // 'data_compare' or 'item_listing'
        groupIndex: null, // For item_listing, the group index
        rowIndex: null, // Row index within group (item_listing) or table (data_compare)
        cellIndex: null, // Cell index within row
        newValue: '' // The new value being edited
      }
    }
  },
  computed: {
    tableHeight() {
      const offset = 240;
      return `calc(100vh - ${offset}px)`;
    }
  },
  methods: {
    async fetchResult() {
      this.loading = true
      try {
        const response = await this.$http.get(`/api/tasks/${this.taskId}/result`)
        this.tableData = response.data
        this.initializeItemListing()
        this.isCompleted = true
      } catch (error) {
        console.error('Error fetching task result:', error)
        alert('Failed to load task result')
      }
      this.loading = false
    },
    initializeItemListing() {
      const origItemListing = this.tableData.item_listing;
      let expandGroupList = []
      let collapseGroupList = []
      let expandGroup = []
      let collapseGroup = []
      let collapsedMatchCell = null
      let collapsedButtonCell = null
      let MatchCellHeight = 0
      const borders = {
        left: {style: "thin", color: null},
        right: {style: "thin", color: null},
        top: {style: "thin", color: null},
        bottom: {style: "thin", color: null},
      }

      // Search the two sections
      let totalSectionStartIdx = 0
      let detailSectionStartIdx = 0

      for (let curRowIdx = 0; curRowIdx < origItemListing.length; curRowIdx++) {
        const curRow = origItemListing[curRowIdx]
        if ((curRow[0].colspan ?? 1) == 9) {
          if ((curRow[0].value ?? "").toLowerCase().includes("details")) {
            detailSectionStartIdx = curRowIdx + 1
            break
          }
        }
      }

      // Total Section
      expandGroup = []
      for (let curRowIdx = 0; curRowIdx < detailSectionStartIdx-1; curRowIdx++) {
        const curRow = origItemListing[curRowIdx]
        curRow[curRow.length-1].colspan = 2
        if ((curRow[0].colspan ?? 1) == 9) {
          if ((curRow[0].value ?? "").toLowerCase().includes("total")) {
            let totalTitleRow = origItemListing[curRowIdx]
            totalTitleRow[0].bold = true
            totalTitleRow[0].fontSize = "20px"
            expandGroup.push(origItemListing[curRowIdx])
          }
        }
        else {
          expandGroup.push(origItemListing[curRowIdx])
        }
      }
      expandGroupList.push(expandGroup)
      collapseGroupList.push(expandGroup)
      expandGroup = []

      // Detail Section
      for (let curRowIdx = detailSectionStartIdx; curRowIdx < origItemListing.length; curRowIdx++) {
        const curRow = origItemListing[curRowIdx]
        let curRowColSpan = 0
        for (let cell of curRow) {
          curRowColSpan += cell.colspan ?? 1
        }
        if (curRowColSpan == 10) {      // The first row of the group, or the document total
          if (expandGroup.length > 0) {
            expandGroupList.push(expandGroup)
            if (collapsedMatchCell) {
              collapsedMatchCell.rowspan = MatchCellHeight
            }
            if (collapsedButtonCell) {
              collapsedButtonCell.rowspan = MatchCellHeight
            }
            collapseGroupList.push(collapseGroup)
          }

          let collapseGroupRow = []
          let matchCell = curRow[curRow.length-1]
          collapsedMatchCell = {value: matchCell.value, colspan: 1, rowspan: 1, borders: matchCell.borders, highlight_color: matchCell.highlight_color}
          collapsedButtonCell = {collapseAction: true, colspan: 1, rowspan: 1, borders}
          let spanCount = 0
          for (let cell of curRow) {
            if ([0, 3, 6].includes(spanCount)) {
              collapseGroupRow.push({value: cell.value, colspan: 3, rowspan: 1, borders})
            }
            spanCount += cell.colspan ?? 1
          }
          let expandedButtonCell = {collapseAction: true, colspan: 1, rowspan: matchCell.rowspan, borders}
          expandGroup = [[...curRow, expandedButtonCell]]
          collapseGroup = [[...collapseGroupRow, collapsedMatchCell, collapsedButtonCell]]
          MatchCellHeight = 1;
        }
        else{
          expandGroup.push(curRow)
          if (curRowColSpan == 9) {
            MatchCellHeight++;
            if (curRow[0].value != "Total:") {  // Should be the start row of non-first item
            // if (curRow.length == 9) {   // Should be the start row of non-first item
              let collapseGroupRow = []
              let spanCount = 0
              for (let cell of curRow) {
                if ([0, 3, 6].includes(spanCount)) {
                  collapseGroupRow.push({value: cell.value, colspan: 3, rowspan: 1, borders})
                }
                spanCount += cell.colspan ?? 1
              }
              collapseGroup.push(collapseGroupRow)
            }
            else {                      // Assume it is first column of total
              collapseGroup.push(curRow)
            }
          }
          else if (curRowColSpan == 6) {
            if (curRow.length == 3) {     // the total count of the item
              MatchCellHeight++;
              collapseGroup.push(curRow)
            }
          }
        }
      }
      expandGroupList.push(expandGroup)
      if (collapsedMatchCell) {
        collapsedMatchCell.rowspan = MatchCellHeight
      }
      if (collapsedButtonCell) {
        collapsedButtonCell.rowspan = MatchCellHeight
      }
      collapseGroupList.push(collapseGroup)
      const isCollapsed = new Array(expandGroupList.length);
      isCollapsed.fill(true)
      this.itemListingData = {
        expand: expandGroupList,
        collapse: collapseGroupList,
        isCollapsed: isCollapsed
      }
    },
    getCellStyle(cell) {
      let style = {}
      if (cell.highlight_color) {
        style.backgroundColor = `#${cell.highlight_color}`
      }
      if (cell.bold) {
        style.fontWeight = 'bold'
      }
      if (cell.italic) {
        style.fontStyle = 'italic'
      }
      if (cell.fontSize) {
        style.fontSize = cell.fontSize
      }
      if (cell.borders) {
        const styleMap = {
          thin: '1px solid',
          medium: '2px solid',
          thick: '3px solid'
        }
        const defaultBorderColor = '#000'
        if (cell.borders.left) {
          style.borderLeft = `${styleMap[cell.borders.left.style] || '1px solid'} ${
            cell.borders.left.color ? `#${cell.borders.left.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.top) {
          style.borderTop = `${styleMap[cell.borders.top.style] || '1px solid'} ${
            cell.borders.top.color ? `#${cell.borders.top.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.bottom) {
          style.borderBottom = `${styleMap[cell.borders.bottom.style] || '1px solid'} ${
            cell.borders.bottom.color ? `#${cell.borders.bottom.color}` : defaultBorderColor
          }`
        }
        if (cell.borders.right) {
          style.borderRight = `${styleMap[cell.borders.right.style] || '1px solid'} ${
            cell.borders.right.color ? `#${cell.borders.right.color}` : defaultBorderColor
          }`
        }
      }
      return style
    },
    updateContainerHeight() {
      this.containerHeight = this.$refs.container ? this.$refs.container.clientHeight : 0
    },
    toggleRowCollapse(rowIndex) {
      this.$set(this.itemListingData.isCollapsed, rowIndex, !this.itemListingData.isCollapsed[rowIndex]);
      // this.itemListingData.isCollapsed[rowIndex] = !this.itemListingData.isCollapsed[rowIndex]
    },
    // New method to open the edit dialog
    openEditDialog(tableType, groupIndex, cellIndex, cell, rowIndex = null) {
      if (cell.field) {
        this.editCell = {
          tableType,
          cell,
          groupIndex,
          rowIndex: rowIndex !== null ? rowIndex : groupIndex, // For data_compare, groupIndex is rowIndex
          cellIndex,
          newValue: cell.value
        }
        this.editDialog = true
      }
    },
    // New method to update the cell value
    async updateValue() {
      try {
        const { tableType, cell, groupIndex, rowIndex, cellIndex, newValue } = this.editCell
        await this.$http.post(`/api/tasks/${this.taskId}/update_value`, {
          field: cell.field,
          value: newValue
        })
        this.editDialog = false
        await this.fetchResult() // Refresh the table data
      } catch (error) {
        console.error('Error updating cell value:', error)
        alert('Failed to update cell value')
      }
    },
    // New method to handle dialog closing and form reset
    closeDialog() {
      this.editDialog = false
      if (this.$refs.editForm) {
        this.$refs.editForm.reset()
      }
    }
  },
  mounted() {
    this.fetchResult()
    this.outputUrl = `${this.$http.defaults.baseURL}/api/tasks/${this.$route.params.id}/export`
    window.addEventListener('resize', this.updateContainerHeight)
    this.updateContainerHeight()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateContainerHeight)
  }
}
</script>
